import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, Eye, FileCheck, XIcon, AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Header } from '../components/Header';
import { copy } from '../copy';
type RedactionRule = {
  id: string;
  type: string;
  find: string;
  replace: string;
  matches: number;
};
export function RedactionPage() {
  const navigate = useNavigate();
  const c = copy.redaction;
  const [rules, setRules] = useState<RedactionRule[]>(c.exampleRules);
  const [showPreview, setShowPreview] = useState(false);
  const [newRule, setNewRule] = useState({
    type: 'name',
    find: '',
    replace: ''
  });
  const addRule = () => {
    if (!newRule.find || !newRule.replace) return;
    const rule: RedactionRule = {
      id: `r${Date.now()}`,
      type: newRule.type,
      find: newRule.find,
      replace: newRule.replace,
      matches: Math.floor(Math.random() * 20) + 1
    };
    setRules([...rules, rule]);
    setNewRule({
      type: 'name',
      find: '',
      replace: ''
    });
  };
  const removeRule = (id: string) => {
    setRules(rules.filter(r => r.id !== id));
  };
  const totalMatches = rules.reduce((sum, r) => sum + r.matches, 0);
  return <div className="min-h-screen bg-white dark:bg-[#1a1a1a] text-slate-900 dark:text-[#e5e5e5] transition-colors duration-200" data-id="element-549">
      <Header title={c.headerTitle} minimal showBackArrow backTo="/case/files" data-id="element-550" />

      <main className="mx-auto max-w-6xl px-6 py-12" data-id="element-551">
        <div className="mb-8" data-id="element-552">
          <h1 className="text-4xl font-bold text-slate-900 dark:text-white tracking-tight" data-id="element-553">
            {c.title}
          </h1>
          <p className="text-base text-slate-500 dark:text-[#a3a3a3] mt-2 leading-relaxed" data-id="element-554">
            {c.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-555">
          {/* Left Column - Rules */}
          <div className="space-y-6" data-id="element-556">
            {/* File Info */}
            <div className="rounded-2xl border border-slate-200 dark:border-[#333] bg-slate-50 dark:bg-[#222] p-5" data-id="element-557">
              <div className="flex items-center gap-3" data-id="element-558">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400" data-id="element-559">
                  <FileCheck size={20} strokeWidth={2} data-id="element-560" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-561">
                  <div className="font-semibold text-slate-900 dark:text-white truncate" data-id="element-562">
                    {c.fileName}
                  </div>
                  <div className="text-sm text-slate-500 dark:text-[#a3a3a3]" data-id="element-563">
                    {totalMatches} {c.matchesLabel}
                  </div>
                </div>
              </div>
            </div>

            {/* Redaction Rules */}
            <section className="rounded-2xl border border-slate-200 dark:border-[#333] bg-slate-50 dark:bg-[#222] p-6 space-y-5" data-id="element-564">
              <div data-id="element-565">
                <h2 className="font-semibold text-base text-slate-900 dark:text-white" data-id="element-566">
                  {c.rulesTitle}
                </h2>
                <p className="text-sm text-slate-500 dark:text-[#a3a3a3] mt-1" data-id="element-567">
                  {c.rulesSubtitle}
                </p>
              </div>

              {/* Existing Rules */}
              <div className="space-y-3" data-id="element-568">
                {rules.map(rule => <motion.div key={rule.id} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                x: -20
              }} className="rounded-xl border border-slate-200 dark:border-[#333] bg-white dark:bg-[#1a1a1a] p-4 space-y-3" data-id="element-569">
                    <div className="flex items-start justify-between gap-3" data-id="element-570">
                      <div className="flex-1 min-w-0 space-y-2" data-id="element-571">
                        <div className="flex items-center gap-2" data-id="element-572">
                          <span className="inline-block px-2 py-0.5 rounded-md bg-slate-100 dark:bg-[#2a2a2a] text-[10px] font-bold uppercase tracking-wider text-slate-600 dark:text-[#a3a3a3]" data-id="element-573">
                            {rule.type}
                          </span>
                          <span className="text-xs font-semibold text-blue-600 dark:text-blue-400" data-id="element-574">
                            {rule.matches} matches
                          </span>
                        </div>
                        <div className="space-y-1" data-id="element-575">
                          <div className="text-sm" data-id="element-576">
                            <span className="text-slate-500 dark:text-[#737373]" data-id="element-577">
                              Find:{' '}
                            </span>
                            <span className="font-medium text-slate-900 dark:text-white" data-id="element-578">
                              {rule.find}
                            </span>
                          </div>
                          <div className="text-sm" data-id="element-579">
                            <span className="text-slate-500 dark:text-[#737373]" data-id="element-580">
                              Replace:{' '}
                            </span>
                            <span className="font-medium text-slate-900 dark:text-white" data-id="element-581">
                              {rule.replace}
                            </span>
                          </div>
                        </div>
                      </div>
                      <button onClick={() => removeRule(rule.id)} className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-slate-400 dark:text-[#555] hover:text-red-600 dark:hover:text-red-400 transition-all active:scale-95" title="Remove rule" data-id="element-582">
                        <Trash2 size={16} strokeWidth={2} data-id="element-583" />
                      </button>
                    </div>
                  </motion.div>)}
              </div>

              {/* Add New Rule */}
              <div className="rounded-xl border-2 border-dashed border-slate-200 dark:border-[#333] bg-white dark:bg-[#1a1a1a] p-4 space-y-4" data-id="element-584">
                <div className="space-y-3" data-id="element-585">
                  <div data-id="element-586">
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-[#555] mb-2" data-id="element-587">
                      {c.placeholders.typeLabel}
                    </label>
                    <select value={newRule.type} onChange={e => setNewRule({
                    ...newRule,
                    type: e.target.value
                  })} className="w-full rounded-xl border border-slate-200 dark:border-[#333] bg-white dark:bg-[#1a1a1a] px-3 py-2.5 text-sm text-slate-600 dark:text-[#a3a3a3] outline-none focus:ring-2 focus:ring-blue-500/20 dark:focus:ring-blue-400/20 focus:border-blue-500 dark:focus:border-blue-400 transition-all" data-id="element-588">
                      {Object.entries(c.ruleTypes).map(([key, label]) => <option key={key} value={key} data-id="element-589">
                          {label}
                        </option>)}
                    </select>
                  </div>
                  <div data-id="element-590">
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-[#555] mb-2" data-id="element-591">
                      {c.placeholders.findLabel}
                    </label>
                    <input type="text" value={newRule.find} onChange={e => setNewRule({
                    ...newRule,
                    find: e.target.value
                  })} placeholder={c.placeholders.findPlaceholder} className="w-full rounded-xl border border-slate-200 dark:border-[#333] bg-white dark:bg-[#1a1a1a] px-3 py-2.5 text-sm text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500/20 dark:focus:ring-blue-400/20 focus:border-blue-500 dark:focus:border-blue-400 transition-all placeholder:text-slate-400 dark:placeholder:text-[#555]" data-id="element-592" />
                  </div>
                  <div data-id="element-593">
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-[#555] mb-2" data-id="element-594">
                      {c.placeholders.replaceLabel}
                    </label>
                    <input type="text" value={newRule.replace} onChange={e => setNewRule({
                    ...newRule,
                    replace: e.target.value
                  })} placeholder={c.placeholders.replacePlaceholder} className="w-full rounded-xl border border-slate-200 dark:border-[#333] bg-white dark:bg-[#1a1a1a] px-3 py-2.5 text-sm text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500/20 dark:focus:ring-blue-400/20 focus:border-blue-500 dark:focus:border-blue-400 transition-all placeholder:text-slate-400 dark:placeholder:text-[#555]" data-id="element-595" />
                  </div>
                </div>
                <button onClick={addRule} disabled={!newRule.find || !newRule.replace} className="flex items-center justify-center gap-2 w-full px-4 py-2.5 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-xl font-semibold hover:bg-slate-800 dark:hover:bg-slate-100 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-596">
                  <Plus size={16} strokeWidth={2.5} data-id="element-597" />
                  {c.addRuleButton}
                </button>
              </div>

              {/* Info Banner */}
              <div className="flex items-start gap-3 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-4" data-id="element-598">
                <AlertCircle size={18} className="shrink-0 mt-0.5 text-blue-600 dark:text-blue-400" strokeWidth={2} data-id="element-599" />
                <p className="text-xs text-blue-700 dark:text-blue-300 leading-relaxed" data-id="element-600">
                  The system automatically detects format variations. For
                  example, a date rule will match "January 1, 1980", "1/1/1980",
                  "01/01/1980", "1/1/80", and "01/01/80".
                </p>
              </div>
            </section>
          </div>

          {/* Right Column - Preview */}
          <div className="space-y-6" data-id="element-601">
            <section className="rounded-2xl border border-slate-200 dark:border-[#333] bg-slate-50 dark:bg-[#222] p-6 space-y-5 h-[calc(100vh-16rem)] flex flex-col" data-id="element-602">
              <div data-id="element-603">
                <h2 className="font-semibold text-base text-slate-900 dark:text-white" data-id="element-604">
                  {c.previewTitle}
                </h2>
                <p className="text-sm text-slate-500 dark:text-[#a3a3a3] mt-1" data-id="element-605">
                  {c.previewSubtitle}
                </p>
              </div>

              {/* Preview Content */}
              <div className="flex-1 rounded-xl border border-slate-200 dark:border-[#333] bg-white dark:bg-[#1a1a1a] p-5 overflow-y-auto" data-id="element-606">
                <pre className="text-sm text-slate-700 dark:text-[#e5e5e5] font-mono leading-relaxed whitespace-pre-wrap" data-id="element-607">
                  {c.previewContent}
                </pre>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-3" data-id="element-608">
                <button onClick={() => navigate('/case/files')} className="flex-1 flex items-center justify-center gap-2 px-4 py-3 border border-slate-200 dark:border-[#333] text-slate-600 dark:text-[#a3a3a3] rounded-xl font-semibold hover:bg-slate-100 dark:hover:bg-white/5 active:scale-[0.98] transition-all" data-id="element-609">
                  <XIcon size={16} strokeWidth={2.5} data-id="element-610" />
                  {c.cancelButton}
                </button>
                <button disabled={rules.length === 0} className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-semibold active:scale-[0.98] transition-all shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-611">
                  <FileCheck size={16} strokeWidth={2.5} data-id="element-612" />
                  {c.generateButton}
                </button>
              </div>
            </section>
          </div>
        </div>
      </main>
    </div>;
}